/**
 * Yoga day drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const yogaDayDrawingsPack: ToilePack = {
    slug: 'yoga-day-drawings',
    category: 'coloring',
    name: 'Yoga day drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Tree pose', subject: 'a person in tree pose on a mat in front of a rising sun'},
        {key: 'salutation', label: 'Sun salutation', subject: 'a row of six figures showing the steps of surya namaskar'},
        {key: 'group', label: 'Group yoga', subject: 'children sitting cross-legged on mats in a park with eyes closed'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['lotus pose', 'a park', 'a teacher'],
    directionHint: 'try: lotus pose',
    seo: {
        title: 'Yoga Day Drawings: Tree Pose, Sun Salutation, Group Yoga',
        metaDescription: 'Nine yoga day drawings: a tree pose, a row of sun salutation steps and children on mats, in pencil, colour and outline.',
        h1: 'Yoga day drawings: tree pose, sun salutation, group yoga',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Yoga day drawings for a competition, a chart or a card. The set shows a person in tree pose on a mat before a rising sun, a row of six figures showing the steps of surya namaskar, and children sitting on mats in a park with their eyes closed. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as lotus pose or a park.',
        useCases: [
            'a yoga day poster for a competition',
            'a chart on yoga poses for a class',
            'a colouring page for a school event',
            'a card for International Yoga Day',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'What is the row of six figures?', a: 'It shows the steps of surya namaskar, the sun salutation sequence, one figure per step.'},
            {q: 'Can I ask for a different pose?', a: 'Yes. Type the pose in the direction field, such as lotus pose. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
